import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api, API_BASE, getCookie} from '../../api';

const VARIABLE_GROUPS = [
  {label: 'Identity', vars: ['{{firstName}}','{{lastName}}','{{middleName}}','{{fullName}}','{{title}}','{{suffix}}','{{employeeId}}','{{dateOfBirth}}']},
  {label: 'Address', vars: ['{{addressLine1}}','{{addressLine2}}','{{city}}','{{state}}','{{zip}}','{{country}}','{{fullAddress}}']},
  {label: 'Dates', vars: ['{{today}}','{{todayLong}}','{{currentYear}}']},
  {label: 'Company', vars: ['{{companyName}}']},
];

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [folders, setFolders] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentFolder = searchParams.get('folder') || null;
  const setCurrentFolder = (folderId) => {
    if (folderId) setSearchParams({folder: folderId});
    else setSearchParams({});
  };
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState(null);
  const [folderModal, setFolderModal] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [openMenu, setOpenMenu] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const navigate = useNavigate();

  const load = () => {
    api('/hcm/document-templates').then(r => r.ok ? r.json() : []).then(setTemplates);
    api('/hcm/document-template-folders').then(r => r.ok ? r.json() : []).then(setFolders);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!openMenu) return;
    const close = () => setOpenMenu(null);
    const timer = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(timer); document.removeEventListener('click', close); };
  }, [openMenu]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const body = { name: name.trim() };
    if (currentFolder) body.folderId = currentFolder;
    const res = await api('/hcm/document-templates', { method: 'POST', body: JSON.stringify(body) });
    if (res.ok) {
      const t = await res.json();
      navigate(`/hcm/templates/${t.id}/edit`);
    }
    setShowCreate(false);
    setName('');
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    if (currentFolder) formData.append('folderId', currentFolder);
    const jwtToken = getCookie('jwtToken');
    const tenantId = getCookie('tenantId');
    const headers = {};
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
    await fetch(`${API_BASE}/hcm/document-templates/upload`, { method: 'POST', headers, body: formData });
    fileRef.current.value = '';
    setUploading(false);
    load();
  };

  const handleDelete = async () => {
    if (!deleting) return;
    await api(`/hcm/document-templates/${deleting.id}`, { method: 'DELETE' });
    setDeleting(null);
    load();
  };

  const handleFolderSubmit = async (e) => {
    e.preventDefault();
    if (!folderName.trim()) return;
    if (folderModal === 'new') {
      await api('/hcm/document-template-folders', { method: 'POST', body: JSON.stringify({ name: folderName.trim() }) });
    } else {
      await api(`/hcm/document-template-folders/${folderModal.id}`, { method: 'PUT', body: JSON.stringify({ name: folderName.trim() }) });
    }
    setFolderModal(null);
    load();
  };

  const handleDeleteFolder = async () => {
    if (!deletingFolder) return;
    await api(`/hcm/document-template-folders/${deletingFolder.id}`, { method: 'DELETE' });
    setDeletingFolder(null);
    if (currentFolder === deletingFolder.id) setCurrentFolder(null);
    load();
  };

  return (
    <Layout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Document Templates</h2>
          {currentFolder && (
            <nav className="flex items-center gap-1 mt-1 text-sm">
              <button onClick={() => setCurrentFolder(null)} className="text-indigo-600 hover:text-indigo-800">All Templates</button>
              <span className="text-gray-400">/</span>
              <span className="text-gray-700 font-medium">{folders.find(f => f.id === currentFolder)?.name}</span>
            </nav>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setFolderModal('new'); setFolderName(''); }}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">New Folder</button>
          <button onClick={() => { setShowCreate(true); setName(''); }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">New Template</button>
          <input type="file" ref={fileRef} onChange={handleUpload} className="hidden" accept=".docx,.doc,.odt" />
          <label onClick={() => fileRef.current.click()}
            className={`px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            {uploading ? 'Uploading...' : 'Upload'}
          </label>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <p className="text-sm text-gray-600 mb-2">Available variables to use in your templates:</p>
        <div className="space-y-2">
          {VARIABLE_GROUPS.map(g => (
            <div key={g.label} className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 w-16">{g.label}</span>
              {g.vars.map(v => (
                <span key={v} className="px-2 py-1 text-xs bg-indigo-50 text-indigo-700 rounded border border-indigo-200 font-mono">{v}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-visible">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Modified</th>
              <th className="px-6 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {!currentFolder && folders.map(f => (
              <tr key={`f-${f.id}`} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <button onClick={() => setCurrentFolder(f.id)} className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium">
                    <svg className="w-5 h-5 text-indigo-400" fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" /></svg>
                    {f.name}
                  </button>
                </td>
                <td className="px-6 py-4 text-gray-400 hidden sm:table-cell">Folder</td>
                <td className="px-6 py-4 text-gray-600">{f.createdAt ? new Date(f.createdAt).toLocaleDateString() : '—'}</td>
                <td className="px-6 py-4">
                  <div className="relative">
                    <button onClick={() => setOpenMenu(openMenu === f.id ? null : f.id)} className="text-gray-400 hover:text-gray-600">⋯</button>
                    {openMenu === f.id && (
                      <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border z-10">
                        <button onClick={() => { setOpenMenu(null); setFolderModal({id: f.id, name: f.name}); setFolderName(f.name); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">Rename</button>
                        <button onClick={() => { setOpenMenu(null); setDeletingFolder(f); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-red-600">Delete</button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {templates.filter(t => currentFolder ? t.folderId === currentFolder : !t.folderId).map(t => (
              <tr key={t.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <button onClick={() => navigate(`/hcm/templates/${t.id}/edit`)} className="text-indigo-600 hover:text-indigo-800 font-medium">{t.name}</button>
                </td>
                <td className="px-6 py-4 hidden sm:table-cell">
                  <span className="inline-flex items-center px-2 py-1 rounded bg-blue-50 text-blue-600 text-xs font-medium">
                    <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                    DOCX
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-600">{t.updatedAt ? new Date(t.updatedAt).toLocaleDateString() : new Date(t.createdAt).toLocaleDateString()}</td>
                <td className="px-6 py-4">
                  <div className="relative">
                    <button onClick={() => setOpenMenu(openMenu === t.id ? null : t.id)} className="text-gray-400 hover:text-gray-600">⋯</button>
                    {openMenu === t.id && (
                      <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border z-10">
                        <button onClick={() => { setOpenMenu(null); navigate(`/hcm/templates/${t.id}/edit`); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">Edit</button>
                        <button onClick={() => { setOpenMenu(null); setDeleting(t); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-red-600">Delete</button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {folders.length === 0 && templates.length === 0 && <div className="text-center py-12 text-gray-500">No templates yet. Create one or upload a file to get started.</div>}
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <form onSubmit={handleCreate} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">New Template</h3>
            <input type="text" value={name} onChange={e => setName(e.target.value)} autoFocus
              placeholder="Template name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!name.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">Create & Edit</button>
            </div>
          </form>
        </div>
      )}

      {folderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setFolderModal(null)}>
          <form onSubmit={handleFolderSubmit} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{folderModal === 'new' ? 'New Folder' : 'Rename Folder'}</h3>
            <input type="text" value={folderName} onChange={e => setFolderName(e.target.value)} autoFocus
              placeholder="Folder name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setFolderModal(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!folderName.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">{folderModal === 'new' ? 'Create' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeleting(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Template</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete "{deleting.name}"? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleting(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {deletingFolder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeletingFolder(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Folder</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete "{deletingFolder.name}"?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeletingFolder(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDeleteFolder} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
